"use client";
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";

export interface User {
  id: string;
  name: string;
  phoneNumber: string;
  email?: string;
  roles: string[];
  cityId?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string, user: User) => void;
  logout: () => void;
}

const TOKEN_KEY = "tb_auth_token";
const USER_KEY = "tb_auth_user";
const isBrowser = typeof window !== "undefined";

const getStoredUser = (): User | null => {
  if (!isBrowser) return null;
  try { return JSON.parse(localStorage.getItem(USER_KEY) || "null"); } catch { return null; }
};

const isTokenValid = (): boolean => {
  if (!isBrowser) return false;
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return false;
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp * 1000 > Date.now();
  } catch { return false; }
};

export const getAuthToken = () => isBrowser ? localStorage.getItem(TOKEN_KEY) : null;

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const storedUser = getStoredUser();
    if (storedUser && isTokenValid()) setUser(storedUser);
    else if (isBrowser) { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(USER_KEY); }
    setIsLoading(false);
  }, []);

  const login = useCallback((token: string, userData: User) => {
    if (isBrowser) { localStorage.setItem(TOKEN_KEY, token); localStorage.setItem(USER_KEY, JSON.stringify(userData)); }
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    if (isBrowser) { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(USER_KEY); }
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: !!user, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
